import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { PaymentDialogComponent } from '../payment-dialog/payment-dialog.component';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface Subscription {
  id: number;
  organization_id: number;
  duration: number;
  total_amount: string;
  users_count: number;
  consultancy_projects: number;
  road: { users_count: number } | null;
  construction: { users_count: number } | null;
  education: { area?: string; users_count: number } | null;
  utilities: { length?: string; users_count: number } | null;
  consultancy: { projects?: number; users_count: number } | null;
  created_at: string;
  payment_status: number;
  membership_start_date: string;
  membership_end_date: string;
  type: string | null;
  branch_id: number | null;
  membershipStatus: {
    status: string;
    isActive: boolean;
    daysRemaining: number;
    daysExpired: number;
    gracePeriodDays: number;
    gracePeriodRemaining: number;
    canAccess: boolean;
    message: string;
  };
  status: string;
  statusDetails: {
    isActive: boolean;
    daysRemaining: number;
    daysExpired: number;
    gracePeriodDays: number;
    gracePeriodRemaining: number;
    canAccess: boolean;
    message: string;
  };
}

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.css']
})
export class PaymentListComponent implements OnInit {
  subscriptions: Subscription[] = [];
  loading = false;
  error: string | null = null;
  orgName: string = '';
  totalStaff: number = 0;
  searchKeyword = '';

  // Stats
  totalPayments = 0;
  totalAmount = 0;
  activeCount = 0;
  expiredCount = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private dialog: MatDialog,
    private apiService: ApiService,
    private toastrService: ToastrMsgService
  ) {}

  ngOnInit() {
    this.loadSubscriptions();
    this.fetchStaff();
  }

  loadSubscriptions() {
    this.loading = true;
    this.error = null;

    const user = localStorage.getItem('user') as any;
    const userParse = JSON.parse(user);
    const orgId = userParse?.user?.org_id;
    this.orgName = userParse?.user?.org_name || 'Organization';

    this.apiService.getSubscriptionsList(orgId).subscribe({
      next: (res: any) => {
        this.subscriptions = res || [];
        this.calculateStats();
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading subscriptions:', err);
        this.error = 'Failed to load payment history';
        this.loading = false;
      }
    });
  }

  calculateStats() {
    this.totalPayments = this.subscriptions.length;
    this.totalAmount = this.subscriptions.reduce(
      (sum, sub) => sum + parseFloat(sub.total_amount || '0'),
      0
    );
    this.activeCount = this.subscriptions.filter(
      (sub) => sub.statusDetails?.isActive || sub.status === 'Active'
    ).length;
    this.expiredCount = this.subscriptions.filter(
      (sub) => !sub.statusDetails?.isActive && sub.status !== 'Active'
    ).length;
  }

  fetchStaff() {
    const params = { keyword: this.searchKeyword || '' };
    this.apiService.getStaff(params).subscribe({
      next: (res: any) => {
        this.totalStaff = res?.data?.length || 0;
      },
      error: (err) => {
        console.error('Error fetching staff:', err);
      }
    });
  }

  // Status helpers
  getStatusClass(sub: Subscription): string {
    if (sub.statusDetails?.isActive || sub.status === 'Active') {
      return 'status-active';
    } else if (sub.statusDetails?.gracePeriodRemaining > 0) {
      return 'status-grace';
    } else {
      return 'status-expired';
    }
  }

  getStatusIcon(sub: Subscription): string {
    if (sub.statusDetails?.isActive || sub.status === 'Active') {
      return 'check_circle';
    } else if (sub.statusDetails?.gracePeriodRemaining > 0) {
      return 'warning';
    } else {
      return 'cancel';
    }
  }

  getStatusText(sub: Subscription): string {
    return sub.status || (sub.statusDetails?.isActive ? 'Active' : 'Expired');
  }

  getPaymentStatusClass(sub: Subscription): string {
    return sub.payment_status === 1 ? 'payment-success' : 'payment-pending';
  }

  getPaymentStatusIcon(sub: Subscription): string {
    return sub.payment_status === 1 ? 'check_circle' : 'pending';
  }

  getPaymentStatusText(sub: Subscription): string {
    return sub.payment_status === 1 ? 'Paid' : 'Pending';
  }

  getTotalUsers(sub: Subscription): number {
    return (
      (sub.road?.users_count || 0) +
      (sub.construction?.users_count || 0) +
      (sub.utilities?.users_count || 0) +
      (sub.consultancy?.users_count || 0) +
      (sub.users_count || 0)
    );
  }

  // Invoice PDF Generation
  downloadInvoice(sub: Subscription) {
    this.toastrService.info('Generating invoice PDF...');

    // Fetch organization details first
    this.apiService.getOrganizations({ keyword: '' }).subscribe({
      next: (res: any) => {
        const organization = res?.data?.find((org: any) => org.org_id === sub.organization_id);
        
        // Create invoice HTML content with organization details
        const invoiceContent = this.generateInvoiceHTML(sub, organization);

    // Create a new window for printing/saving
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(invoiceContent);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
    } else {
      this.toastrService.error('Please allow popups to download the invoice');
    }
      },
      error: (err: any) => {
        console.error('Error fetching organization details:', err);
        this.toastrService.error('Failed to fetch organization details');
        // Fallback: generate invoice without organization details
        const invoiceContent = this.generateInvoiceHTML(sub, null);
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (printWindow) {
          printWindow.document.write(invoiceContent);
          printWindow.document.close();
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 250);
          };
        }
      }
    });
  }

  generateInvoiceHTML(sub: Subscription, organization: any = null): string {
    const invoiceDate = new Date(sub.created_at).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const startDate = new Date(sub.membership_start_date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const endDate = new Date(sub.membership_end_date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Format organization address
    const formatAddress = (org: any): string => {
      if (!org) return '';
      const parts = [];
      if (org.address) parts.push(org.address);
      if (org.address_line_1) parts.push(org.address_line_1);
      if (org.address_line_2) parts.push(org.address_line_2);
      if (org.city) parts.push(org.city);
      if (org.state) parts.push(org.state);
      if (org.postal_code) parts.push(org.postal_code);
      if (org.country) parts.push(org.country);
      return parts.filter(p => p).join(', ');
    };

    const orgAddress = organization ? formatAddress(organization) : '';
    const orgName = organization?.org_name || this.orgName;
    const orgContactName = organization?.primary_contact_name || '';
    const orgContactEmail = organization?.primary_contact_email || '';
    const orgContactPhone = organization?.primary_contact_phone || '';
    const orgGST = organization?.gst_number || '';
    const orgPAN = organization?.pan_number || '';
    const orgRegistration = organization?.registration_number || '';

    // Calculate GST breakdown (GST is already included in total_amount)
    const totalAmount = parseFloat(sub.total_amount);
    // GST = Total * (18/118) since GST is 18% of base and total includes GST
    const gstAmount = totalAmount * (18 / 118);
    const subtotal = totalAmount - gstAmount;

    return `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice #INV-${sub.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; background: #fff; color: #333; }
    .invoice-container { max-width: 800px; margin: 0 auto; }
    .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #3b82f6; }
    .company-info h1 { font-size: 28px; color: #1e40af; margin-bottom: 8px; }
    .company-info p { color: #64748b; font-size: 14px; }
    .invoice-info { text-align: right; }
    .invoice-info h2 { font-size: 32px; color: #1e40af; margin-bottom: 8px; }
    .invoice-info p { color: #64748b; font-size: 14px; margin-bottom: 4px; }
    .invoice-info .invoice-number { font-size: 16px; font-weight: 600; color: #334155; }
    .billing-section { display: flex; justify-content: space-between; margin-bottom: 40px; }
    .billing-to, .billing-details { flex: 1; }
    .billing-to h3, .billing-details h3 { font-size: 14px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; }
    .billing-to p, .billing-details p { font-size: 15px; color: #334155; margin-bottom: 6px; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
    .items-table th { background: #f1f5f9; padding: 14px 16px; text-align: left; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; color: #475569; border-bottom: 2px solid #e2e8f0; }
    .items-table td { padding: 16px; border-bottom: 1px solid #e2e8f0; font-size: 14px; color: #334155; }
    .items-table .amount { text-align: right; font-weight: 600; }
    .totals { margin-left: auto; width: 300px; }
    .totals-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e2e8f0; }
    .totals-row.total { border-bottom: none; border-top: 2px solid #334155; padding-top: 15px; margin-top: 5px; }
    .totals-row .label { color: #64748b; }
    .totals-row .value { font-weight: 600; color: #334155; }
    .totals-row.total .value { font-size: 20px; color: #1e40af; }
    .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 12px; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
    .status-paid { background: #dcfce7; color: #166534; }
    .status-pending { background: #fef3c7; color: #92400e; }
    @media print {
      body { padding: 20px; }
      .invoice-container { max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="invoice-header">
      <div class="company-info">
        <h2>SigmaT Consultancy Private Limited</h1>
       
        <p>01, B-17, Panchsheel, Opposite Saraswat Bank, Mira Road, Sector 10,</p>
        <p> Shanti Nagar, Thane – 401107, Maharashtra, India</p>
        <p>
Avinash.rai@sigmat.in | Mobile: +91 8976402839</p>
<p>GSTIN :  27ABBCS1890D1ZF</p>
      </div>
      <div class="invoice-info">
        <h2>INVOICE</h2>
        <p class="invoice-number">Invoice #INV-${sub.id}</p>
        <p>Date: ${invoiceDate}</p>
        <p><span class="status-badge ${sub.payment_status === 1 ? 'status-paid' : 'status-pending'}">${sub.payment_status === 1 ? 'PAID' : 'PENDING'}</span></p>
      </div>
    </div>

    <div class="billing-section">
      <div style="font-size: 14px;" class="billing-to">
        <h3>Bill To</h3>
        <p ><strong>${orgName}</strong></p>
        ${orgAddress ? `<p>${orgAddress}</p>` : ''}
        ${orgContactName ? `<p>Contact: ${orgContactName}</p>` : ''}
        ${orgContactEmail ? `<p>Email: ${orgContactEmail}</p>` : ''}
        ${orgContactPhone ? `<p>Phone: ${orgContactPhone}</p>` : ''}
        ${orgGST ? `<p>GSTIN: ${orgGST}</p>` : ''}
        ${orgPAN ? `<p>PAN: ${orgPAN}</p>` : ''}
      </div>
      <div class="invoice-info" style="float: right;">
        <h3>Subscription Period</h3>
        <p><strong>Start:</strong> ${startDate}</p>
        <p><strong>End:</strong> ${endDate}</p>
        <p><strong>Duration:</strong> ${sub.duration} months</p>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th>Description</th>
          <th>Users</th>
          <th>Duration</th>
          <th class="amount">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${sub.road && sub.road.users_count > 0 ? `
        <tr>
          <td>Road Infrastructure Module</td>
          <td>${sub.road.users_count} users</td>
          <td>${sub.duration} months</td>
          <td class="amount">Included</td>
        </tr>` : ''}
        ${sub.construction && sub.construction.users_count > 0 ? `
        <tr>
          <td>Construction Module</td>
          <td>${sub.construction.users_count} users</td>
          <td>${sub.duration} months</td>
          <td class="amount">Included</td>
        </tr>` : ''}
        ${sub.utilities && sub.utilities.users_count > 0 ? `
        <tr>
          <td>Utilities Module</td>
          <td>${sub.utilities.users_count} users</td>
          <td>${sub.duration} months</td>
          <td class="amount">Included</td>
        </tr>` : ''}
        ${sub.consultancy && sub.consultancy.users_count > 0 ? `
        <tr>
          <td>Consultancy Module</td>
          <td>${sub.consultancy.users_count} users</td>
          <td>${sub.duration} months</td>
          <td class="amount">Included</td>
        </tr>` : ''}
        <tr>
          <td><strong>Subscription Plan - ${sub.duration} Months</strong></td>
          <td><strong>${this.getTotalUsers(sub)} total users</strong></td>
          <td>${sub.duration} months</td>
          <td class="amount">₹${parseFloat(sub.total_amount).toLocaleString('en-IN')}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals" style="margin-right: 12px">
      <div class="totals-row">
        <span class="label">Subtotal</span>
        <span class="value">₹${subtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
      </div>
      <div class="totals-row">
        <span class="label">Tax (GST 18%)</span>
        <span class="value">₹${gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
      </div>
      <div class="totals-row total">
        <span class="label">Total</span>
        <span class="value">₹${totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
      </div>
    </div>

    <div class="footer">
      <p>Thank you for your business!</p>
      <p>This is a computer-generated invoice and does not require a signature.</p>
    </div>
  </div>
</body>
</html>`;
  }

  // Dialog actions
  openAddDialog() {
    const dialogRef = this.dialog.open(PaymentDialogComponent, {
      width: '400px',
      data: null
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadSubscriptions();
      }
    });
  }

  viewDetails(sub: Subscription) {
    // Could open a detailed view dialog
    console.log('View details:', sub);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.subscriptions = this.subscriptions.filter(
      (sub) =>
        sub.id.toString().includes(filterValue) ||
        sub.total_amount.includes(filterValue) ||
        sub.status?.toLowerCase().includes(filterValue)
    );

    if (!filterValue) {
      this.loadSubscriptions();
    }
  }
}
